import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLegalLine } from "@/components/AuthLegalLine";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(u.role === "admin" ? "/admin" : u.role === "vendor" ? "/vendor/dashboard" : "/planner/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  // Google OAuth is intentionally DISABLED for initial launch. This helper is
  // kept intact so the P1 re-enablement is a small change: toggle
  // GOOGLE_AUTH_ENABLED on the backend and un-hide the button below. Do not
  // delete.
  // eslint-disable-next-line no-unused-vars
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] grid xl:grid-cols-2">
      <div className="hidden xl:block relative overflow-hidden">
        <img src="https://images.pexels.com/photos/12954015/pexels-photo-12954015.jpeg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute bottom-12 left-12 text-white">
          <div className="eyebrow text-white/80 mb-3">Welcome back</div>
          <h2 className="font-serif text-5xl">The details<br/>are the design.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="eyebrow mb-3">Sign in</div>
          <h1 className="font-serif text-4xl sm:text-5xl mb-8 sm:mb-10">Enter NContro</h1>

          {/* Google OAuth intentionally hidden for initial launch. See googleLogin() above — P1 re-enablement checklist. */}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="eyebrow">Email</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="mt-2 h-12 bg-white" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="eyebrow">Password</Label>
                <Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs link-underline text-[color:var(--text-muted)]">Forgot password?</Link>
              </div>
              <Input data-testid="login-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} className="mt-2 h-12 bg-white" />
            </div>
            <Button data-testid="login-submit" disabled={loading} className="btn-sage rounded-full w-full h-12 mt-2">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="text-sm text-[#5C5C5C] mt-8">
            New to NContro? <Link to="/register" className="link-underline text-black">Create an account</Link>
          </div>
          <AuthLegalLine className="mt-6" />
        </div>
      </div>
    </div>
  );
}
